import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Add } from '@mui/icons-material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import PeopleIcon from '@mui/icons-material/People';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SecurityIcon from '@mui/icons-material/Security';

import Tenants from './Tenants';
import Roles from './Roles';
import Users from './Users';
import { useTheme, alpha } from '@mui/material/styles';
import './UserManagement.scss';

function TabPanel(props: {
  children?: React.ReactNode;
  value: number;
  index: number;
  [key: string]: unknown;
}) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other} style={{ flex: 1, minHeight: 0 }}>
      {value === index && (
        <Box p={2} sx={{ height: '100%', boxSizing: 'border-box' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function UserManagement() {
  const theme = useTheme();
  const ACCENT = theme.tokens.accent;
  const iconBtnSx = {
    color: ACCENT,
    opacity: 0.7,
    '&:hover': { opacity: 1, backgroundColor: alpha(ACCENT, 0.08) },
  } as const;
  const [value, setValue] = React.useState(0);

  const addRef = React.useRef<() => void>(() => {});
  const exportRef = React.useRef<() => void>(() => {});
  const toggleFiltersRef = React.useRef<() => void>(() => {});
  const toggleFullScreenRef = React.useRef<() => void>(() => {});

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const labels = ['User', 'Tenant', 'Role'];

  const handleMount = React.useCallback(
    (actions: {
      add: () => void;
      export: () => void;
      toggleFilters: () => void;
      toggleFullScreen: () => void;
    }) => {
      addRef.current = actions.add;
      exportRef.current = actions.export;
      toggleFiltersRef.current = actions.toggleFilters;
      toggleFullScreenRef.current = actions.toggleFullScreen;
    },
    []
  );

  return (
    <div className="um-container" id="user-management-container">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid var(--t-border, rgba(0,0,0,0.12))',
          flexShrink: 0,
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          id="user-management-tabs"
          aria-label="User management navigation"
          sx={{ flex: 1 }}
        >
          <Tab
            icon={<PeopleIcon fontSize="small" />}
            iconPosition="start"
            label="Users"
            id="user-management-users-tab"
            aria-controls="user-management-users-tab-panel"
          />
          <Tab
            icon={<ApartmentIcon fontSize="small" />}
            iconPosition="start"
            label="Tenants"
            id="user-management-tenants-tab"
            aria-controls="user-management-tenants-tab-panel"
          />
          <Tab
            icon={<SecurityIcon fontSize="small" />}
            iconPosition="start"
            label="Roles"
            id="user-management-roles-tab"
            aria-controls="user-management-roles-tab-panel"
          />
        </Tabs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pr: 0.5 }}>
          <Tooltip title="Toggle Filters">
            <IconButton size="small" onClick={() => toggleFiltersRef.current?.()} sx={iconBtnSx}>
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Fullscreen">
            <IconButton size="small" onClick={() => toggleFullScreenRef.current?.()} sx={iconBtnSx}>
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Data">
            <IconButton size="small" onClick={() => exportRef.current?.()} sx={iconBtnSx}>
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => addRef.current?.()}
            sx={{
              backgroundColor: ACCENT,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12,
              px: 1.5,
              ml: 0.5,
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: theme.tokens.accentHover },
            }}
          >
            Add {labels[value]}
          </Button>
        </Box>
      </Box>

      <TabPanel value={value} index={0} id="user-management-users-tab-panel">
        <Users onMount={handleMount} />
      </TabPanel>
      <TabPanel value={value} index={1} id="user-management-tenants-tab-panel">
        <Tenants onMount={handleMount} />
      </TabPanel>
      <TabPanel value={value} index={2} id="user-management-roles-tab-panel">
        <Roles onMount={handleMount} />
      </TabPanel>
    </div>
  );
}
